import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { fetchDeliveryChallans, deleteDeliveryChallan, clearError } from '../../store/deliveryChallanSlice';
import { fetchColleges } from '../../store/collegeSlice';
import DeliveryChallanForm from './DeliveryChallanForm';
import deliveryChallanService from '../../services/deliveryChallanService';

const DeliveryChallanList = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { list, loading, error, pagination } = useSelector((state) => state.deliveryChallan);
  const { list: colleges } = useSelector((state) => state.colleges);
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.userType === 1;
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCollege, setSelectedCollege] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedChallan, setSelectedChallan] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [challanToDelete, setChallanToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuChallan, setMenuChallan] = useState(null);

  useEffect(() => {
    dispatch(fetchColleges({ limit: 100 }));
    dispatch(fetchDeliveryChallans({ page: 1, limit: 10, search: searchTerm, collegeId: selectedCollege }));
  }, [dispatch, searchTerm, selectedCollege]);

  useEffect(() => {
    if (error) {
      setSnackbar({ open: true, message: error, severity: 'error' });
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCollegeFilter = (event) => {
    setSelectedCollege(event.target.value);
  };

  const handleAddChallan = () => {
    setSelectedChallan(null);
    setOpenDialog(true);
  };

  const handleEditChallan = (challan) => {
    setSelectedChallan(challan);
    setOpenDialog(true);
    setAnchorEl(null);
  };

  const handleDeleteClick = (challan) => {
    setChallanToDelete(challan);
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDeleteConfirm = async () => {
    if (challanToDelete) {
      await dispatch(deleteDeliveryChallan(challanToDelete._id));
      setSnackbar({ open: true, message: 'Delivery challan deleted successfully', severity: 'success' });
      setDeleteDialogOpen(false);
      setChallanToDelete(null);
    }
  };

  const handleGeneratePDF = async (challan) => {
    try {
      const pdfBlob = await deliveryChallanService.generatePDF(challan._id);
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Challan-${challan.challanNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'PDF generated successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to generate PDF', severity: 'error' });
    }
    setAnchorEl(null);
  };

  const handleMenuClick = (event, challan) => {
    setAnchorEl(event.currentTarget);
    setMenuChallan(challan);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuChallan(null);
  };

  const columns = [
    { field: 'challanNumber', headerName: 'Challan Number', width: 150, flex: 1 },
    { 
      field: 'collegeId', 
      headerName: 'College', 
      width: 200, 
      flex: 1,
      renderCell: (params) => params.row.collegeId?.name || 'N/A'
    },
    { 
      field: 'date', 
      headerName: 'Date', 
      width: 120,
      renderCell: (params) => new Date(params.value).toLocaleDateString()
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={`₹${params.value.toLocaleString()}`}
          color="primary"
          variant="outlined"
          size="small"
        />
      )
    },
    {
      field: 'details',
      headerName: 'Items',
      width: 80,
      renderCell: (params) => (
        <Chip
          label={params.row.details?.length || 0}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuClick(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  const MobileCard = ({ challan }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              <ReceiptIcon sx={{ mr: 1, color: 'primary.main' }} />
              {challan.challanNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {challan.collegeId?.name}
            </Typography>
          </Box>
          <IconButton size="small" onClick={(e) => handleMenuClick(e, challan)}>
            <MoreVertIcon />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={`₹${challan.amount.toLocaleString()}`}
            color="primary"
            size="small"
          />
          <Chip
            label={`${challan.details?.length || 0} Items`}
            size="small"
            variant="outlined"
          />
        </Box>
        
        <Typography variant="caption" color="text.secondary">
          Date: {new Date(challan.date).toLocaleDateString()}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Delivery Challans
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddChallan}
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
        >
          Create Challan
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Search Challan Number..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Filter by College</InputLabel>
            <Select
              value={selectedCollege}
              onChange={handleCollegeFilter}
              label="Filter by College"
            >
              <MenuItem value="">All Colleges</MenuItem>
              {colleges.map((college) => (
                <MenuItem key={college._id} value={college._id}>
                  {college.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {isMobile ? (
        <Box>
          {loading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <Card key={index} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ height: 20, bgcolor: 'grey.300', borderRadius: 1, mb: 1 }} />
                      <Box sx={{ height: 16, bgcolor: 'grey.200', borderRadius: 1, width: '60%' }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))
          ) : (
            list.map((challan) => <MobileCard key={challan._id} challan={challan} />)
          )}
        </Box>
      ) : (
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={list}
            columns={columns}
            loading={loading}
            pagination
            paginationMode="server"
            rowCount={pagination.total}
            pageSize={pagination.limit}
            page={pagination.page - 1}
            onPageChange={(newPage) => {
              dispatch(fetchDeliveryChallans({ 
                page: newPage + 1, 
                limit: pagination.limit, 
                search: searchTerm,
                collegeId: selectedCollege
              }));
            }}
            getRowId={(row) => row._id}
            disableSelectionOnClick
          />
        </Paper>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditChallan(menuChallan)}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleGeneratePDF(menuChallan)}>
          <PdfIcon sx={{ mr: 1 }} />
          Generate PDF
        </MenuItem>
        {isAdmin && (
          <MenuItem onClick={() => handleDeleteClick(menuChallan)} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {selectedChallan ? 'Edit Delivery Challan' : 'Create New Delivery Challan'}
        </DialogTitle>
        <DialogContent>
          <DeliveryChallanForm
            challan={selectedChallan}
            onSuccess={(message) => {
              setSnackbar({ open: true, message, severity: 'success' });
              setOpenDialog(false);
            }}
            onCancel={() => setOpenDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete challan "{challanToDelete?.challanNumber}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeliveryChallanList;